import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropperProps {
  image: File;
  aspectRatio?: number;
  onCropComplete: (croppedImage: File) => void;
  onCancel: () => void;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  image,
  aspectRatio = 16 / 9,
  onCropComplete,
  onCancel,
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: (90 / aspectRatio),
    x: 5,
    y: 5,
  });
  const [imageSrc, setImageSrc] = useState<string>('');
  const imgRef = useRef<HTMLImageElement>(null);

  // Load image when component mounts
  React.useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
    };
    reader.readAsDataURL(image);
  }, [image]);

  const onImageLoad = useCallback((img: HTMLImageElement) => {
    // Do not directly assign to imgRef.current
    // Just use the image parameters for setting crop dimensions
    const width = img.width;
    const height = img.height;
    const cropWidth = 90;
    const cropHeight = (cropWidth / aspectRatio);
    
    setCrop({
      unit: '%',
      width: cropWidth,
      height: cropHeight,
      x: (100 - cropWidth) / 2,
      y: (100 - cropHeight) / 2,
    });
  }, [aspectRatio]);

  const getCroppedImg = useCallback(async (
    image: HTMLImageElement,
    crop: PixelCrop,
  ): Promise<File> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Set the canvas size to the cropped size multiplied by the scale
    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = crop.width * scaleX * pixelRatio;
    canvas.height = crop.height * scaleY * pixelRatio;

    // Scale the context to handle high DPI displays
    ctx.scale(pixelRatio, pixelRatio);

    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY,
    );

    // Determine the best format based on browser support and original image type
    const format = image.src.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
    const quality = format === 'image/png' ? undefined : 0.95;

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas is empty');
        }
        const fileName = image.src.split('/').pop() || 'cropped-image';
        const fileExtension = format === 'image/png' ? '.png' : '.jpg';
        const croppedFile = new File(
          [blob],
          fileName.replace(/\.[^/.]+$/, '') + fileExtension,
          {
            type: format,
          }
        );
        resolve(croppedFile);
      }, format, quality);
    });
  }, []);

  const handleCropComplete = useCallback(() => {
    const imageElement = imgRef.current;
    if (!imageElement || !crop.width || !crop.height) {
      console.error("Image or crop dimensions missing");
      return;
    }

    // Use getCroppedImg function we already have
    getCroppedImg(imageElement, crop as PixelCrop)
      .then(croppedFile => {
        onCropComplete(croppedFile);
      })
      .catch(error => {
        console.error("Error cropping image:", error);
      });
  }, [crop, getCroppedImg, onCropComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Crop Image</h3>
          <p className="mt-1 text-sm text-gray-500">
            Adjust the crop area to select the portion of the image you want to use.
          </p>
        </div>
        
        <div className="p-4">
          <div className="relative max-h-[60vh] overflow-auto">
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                aspect={aspectRatio}
                className="max-w-full"
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop me"
                  onLoad={(e) => onImageLoad(e.currentTarget)}
                  className="max-w-full"
                />
              </ReactCrop>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCropComplete}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper; 